import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SEO } from '../../components/common/SEO';

// This would normally come from a CMS or API
const blogPosts = [
  {
    slug: 'christmas-gift-guide-2025',
    title: 'Ultimate Christmas Gift Guide 2025: 100+ Perfect Gift Ideas',
    description: 'Discover the perfect Christmas gifts for everyone on your list. From budget-friendly stocking stuffers to luxury presents, plus money-saving tips!',
    publishedAt: '2024-11-01',
    readingTime: '12 min read',
    image: '/images/blog/christmas-gift-guide-2025.jpg',
    featured: true,
    category: 'Gift Guides'
  },
  {
    slug: 'secret-santa-ideas-under-20',
    title: 'Secret Santa Ideas Under $20: Budget-Friendly Gift Guide',
    description: 'Find amazing Secret Santa gifts that won\'t break the bank. Perfect for office parties and friend exchanges.',
    publishedAt: '2024-10-15',
    readingTime: '8 min read',
    image: '/images/blog/secret-santa-ideas.jpg',
    featured: false,
    category: 'Gift Guides'
  },
  {
    slug: 'price-tracking-tips',
    title: 'How to Track Prices and Never Miss a Sale Again',
    description: 'Learn the best strategies for price tracking and getting notified when your favorite items go on sale.',
    publishedAt: '2024-10-01',
    readingTime: '6 min read',
    image: '/images/blog/price-tracking.jpg',
    featured: false,
    category: 'Tips & Tricks'
  }
];

export const BlogHomePage: React.FC = () => {
  const featuredPost = blogPosts.find(post => post.featured);
  const otherPosts = blogPosts.filter(post => !post.featured);

  return (
    <>
      <SEO
        title="Gift Planning Blog - Tips, Guides & Ideas"
        description="Expert tips, gift guides, and planning advice for every occasion. Learn how to be the best gift-giver with our comprehensive blog."
        keywords={['gift planning blog', 'gift guides', 'holiday tips', 'gift ideas', 'christmas gifts', 'birthday gifts']}
        url="https://getwishful.com/blog"
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Gift Planning Blog
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Expert tips, gift guides, and planning advice to help you become 
              the ultimate gift-giver. Never struggle with gift ideas again.
            </p>
          </div>

          {/* Featured Post */}
          {featuredPost && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Article</h2>
              <Link to={`/blog/${featuredPost.slug}`}>
                <Card hover className="overflow-hidden">
                  <div className="md:flex">
                    <div className="md:w-1/2">
                      <img
                        src={featuredPost.image}
                        alt={featuredPost.title}
                        className="w-full h-64 md:h-full object-cover"
                      />
                    </div>
                    <div className="md:w-1/2 p-8">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                        <span className="bg-brown-100 text-brown-800 px-2 py-1 rounded">
                          {featuredPost.category}
                        </span>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(featuredPost.publishedAt).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {featuredPost.readingTime}
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {featuredPost.title}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {featuredPost.description}
                      </p>
                      <Button>
                        Read Article
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </Link>
            </section>
          )}

          {/* All Posts */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Latest Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherPosts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`}>
                  <Card hover className="overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <span className="bg-brown-100 text-brown-800 px-2 py-1 rounded">
                          {post.category}
                        </span>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(post.publishedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {post.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          {post.readingTime}
                        </div>
                        <Button variant="outline" size="sm">
                          Read More
                        </Button>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>

          {/* Categories */}
          <section className="mb-12">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse by Category</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Gift Guides', 'Tips & Tricks', 'Holiday Planning', 'Budget Gifts', 'Luxury Gifts', 'Tech Gifts', 'DIY Gifts', 'Seasonal'].map((category) => (
                  <Link key={category} to={`/blog/category/${category.toLowerCase().replace(' ', '-')}`}>
                    <Card hover className="text-center p-4">
                      <h3 className="font-semibold text-gray-900">{category}</h3>
                    </Card>
                  </Link>
                ))}
              </div>
            </Card>
          </section>

          {/* CTA */}
          <section className="text-center">
            <Card className="p-8 bg-white">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Start Planning?
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Put these tips into practice with your own wishlist and gift planning.
              </p>
              <Link to="/dashboard">
                <Button size="lg">
                  Create Your Wishlist
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </Card>
          </section>
        </div>
      </div>
    </>
  );
};
